import {
    useAddChannelAdminContactMutations,
    useAddSiteAdminContactMutations,
    useDeleteChannelAdminContactMutations,
    useDeleteSiteAdminContactMutations,
} from '@/shared/api'
import { SiteLinkPublisherWithAdminContacts } from '@/shared/api/site-links-group/site-links-group.types'
import { InviteLinkPublisherWithAdminContacts } from '@/shared/api/invite-links-group/invite-links-group.types'
import { Input } from '@/shared/ui'
import { InviteLinkAdminContact } from '@synapex/prisma/client'
import {
    CheckIcon,
    SquareArrowOutUpRightIcon,
    Plus,
    TrashIcon,
    XIcon,
} from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import './AdminsContacts.scss'

interface AdminsContactsProps {
    publisher:
        | SiteLinkPublisherWithAdminContacts
        | InviteLinkPublisherWithAdminContacts
        | undefined
    groupId: number
    inviteLinkId?: number
    siteLinkId?: number
    handleSelectedAdminContactIdChange: (
        editingRowId: number,
        selectedAdminContactId: number | null
    ) => void
}
export const AdminsContacts = ({
    groupId,
    publisher,
    inviteLinkId,
    siteLinkId,
    handleSelectedAdminContactIdChange,
}: AdminsContactsProps) => {
    const [open, setOpen] = React.useState(false)
    const [inputValue, setInputValue] = useState('')
    const inputRef = useRef<HTMLInputElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const [selectedContact, setSelectedContact] =
        useState<InviteLinkAdminContact | null>(null)

    const link_selected_admin_contact_id =
        publisher && 'site_link_selected_admin_contact_id' in publisher
            ? publisher.site_link_selected_admin_contact_id
            : publisher?.invite_link_selected_admin_contact_id

    useEffect(() => {
        setSelectedContact(
            publisher?.admins_contacts.find(
                (contact) =>
                    contact.admin_contact_id === link_selected_admin_contact_id
            ) || null
        )
    }, [publisher, link_selected_admin_contact_id])

    const addChannelContact = useAddChannelAdminContactMutations(groupId)
    const addSiteContact = useAddSiteAdminContactMutations(groupId)

    const [inputError, setInputError] = useState(false)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                contentRef.current &&
                !contentRef.current.contains(event.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(event.target as Node)
            ) {
                setOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const handleSelectAdmin = useCallback(
        (admin_contact_id: number) => {
            if (inviteLinkId) {
                handleSelectedAdminContactIdChange(
                    inviteLinkId,
                    admin_contact_id
                )
            }

            if (siteLinkId) {
                handleSelectedAdminContactIdChange(siteLinkId, admin_contact_id)
            }
        },
        [handleSelectedAdminContactIdChange, inviteLinkId, siteLinkId]
    )

    const handleAddNewContact = useCallback(() => {
        if (inviteLinkId && inputValue.length > 1) {
            setInputError(false)
            addChannelContact.mutate(
                {
                    inviteLinkId,
                    telegramUsername: inputValue,
                    shouldSelectAdmin: true,
                },
                {
                    onSuccess: () => {
                        setInputValue('')
                        setOpen(false)
                    },
                    onError: () => {
                        setInputError(true)
                        setOpen(false)
                    },
                }
            )
        }

        if (siteLinkId && inputValue.length > 1) {
            setInputError(false)
            addSiteContact.mutate(
                {
                    siteLinkId,
                    telegramUsername: inputValue,
                    shouldSelectAdmin: true,
                },
                {
                    onSuccess: () => {
                        setInputValue('')
                        setOpen(false)
                    },
                    onError: () => {
                        setInputError(true)
                        setOpen(false)
                    },
                }
            )
        }
    }, [
        inviteLinkId,
        inputValue,
        siteLinkId,
        addChannelContact,
        addSiteContact,
    ])

    const deleteChannelContact = useDeleteChannelAdminContactMutations(groupId)
    const deleteSiteContact = useDeleteSiteAdminContactMutations(groupId)

    const [contactIdToDelete, setContactIdToDelete] = React.useState<
        number | null
    >(null)

    const [selectedContactIdToDelete, setSelectedContactIdToDelete] =
        React.useState<boolean>(false)

    const handleDeleteContact = useCallback(() => {
        if (inviteLinkId) {
            deleteChannelContact.mutate(
                { inviteLinkId, adminContactId: contactIdToDelete },
                {
                    onSuccess: () => {
                        setContactIdToDelete(null)
                    },
                }
            )
        }

        if (siteLinkId) {
            deleteSiteContact.mutate(
                { siteLinkId, adminContactId: contactIdToDelete },
                {
                    onSuccess: () => {
                        setContactIdToDelete(null)
                    },
                }
            )
        }
    }, [
        contactIdToDelete,
        deleteChannelContact,
        deleteSiteContact,
        inviteLinkId,
        siteLinkId,
    ])

    const handleDiselectAdmin = useCallback(() => {
        setSelectedContactIdToDelete(false)
        setSelectedContact(null)

        if (inviteLinkId) {
            handleSelectedAdminContactIdChange(inviteLinkId, null)
        }

        if (siteLinkId) {
            handleSelectedAdminContactIdChange(siteLinkId, null)
        }
    }, [handleSelectedAdminContactIdChange, inviteLinkId, siteLinkId])

    const getLinkToTgUser = (contact: InviteLinkAdminContact) => {
        if (contact.telegram_username) {
            return `https://t.me/${contact.telegram_username.slice(1)}`
        }
        return `tg://user?id=${contact.telegram_user_id}`
    }

    return (
        <>
            <div className="admin-contacts relative">
                {selectedContact ? (
                    selectedContactIdToDelete ? (
                        <div className="admin-contacts__selected-contact admin-contacts__selected-contact--delete-status">
                            <span className="admin-contacts__delete-confirmation">
                                Удалить?
                            </span>
                            <div className="admin-contacts__list-item-icons">
                                <div
                                    onClick={handleDiselectAdmin}
                                    className="admin-contacts__delete-confirmation-icon cursor-pointer"
                                >
                                    <CheckIcon />
                                </div>
                                <div
                                    onClick={() =>
                                        setSelectedContactIdToDelete(false)
                                    }
                                    className="cursor-pointer pl-[8px]"
                                >
                                    <XIcon />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="admin-contacts__selected-contact">
                            <a
                                href={getLinkToTgUser(selectedContact)}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <span className="admin-contacts__selected-contact__username">
                                    {selectedContact.telegram_username}
                                </span>
                            </a>
                            <div
                                className="admin-contacts__selected-contact__delete-icon"
                                onClick={() =>
                                    setSelectedContactIdToDelete(true)
                                }
                            >
                                <TrashIcon size={20} />
                            </div>
                        </div>
                    )
                ) : (
                    <div className="relative">
                        <Input
                            ref={inputRef}
                            onFocus={() => {
                                setOpen(true)
                                setInputError(false)
                            }}
                            value={inputValue}
                            onChange={(value) => {
                                if (value === '') {
                                    setInputValue('')
                                } else if (!value.startsWith('@')) {
                                    setInputValue('@' + value)
                                } else {
                                    setInputValue(value)
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault()
                                    handleAddNewContact()
                                }
                            }}
                            innerClassName="h-[32px] !pr-6"
                            placeholder="Введите @никнейм"
                            shouldShowClearButton={false}
                            error={inputError}
                        />
                        {inputValue && (
                            <div
                                className="absolute right-2 top-1/2 z-[1000] -translate-y-1/2 cursor-pointer"
                                onClick={handleAddNewContact}
                            >
                                <Plus size={13} className="text-blue-primary" />
                            </div>
                        )}
                    </div>
                )}

                {inputError && !selectedContact && (
                    <div className="absolute -bottom-[20px] text-[10px] text-red-500">
                        Пользователь не найден
                    </div>
                )}
                {open && !selectedContact && (
                    <div
                        ref={contentRef}
                        className="admin-contacts__content absolute left-0 top-full z-50 mt-1 bg-white"
                        style={{
                            width: inputRef.current?.offsetWidth,
                            minWidth: '200px',
                        }}
                    >
                        <div>
                            <div className="admin-contacts__header">
                                Контакты администраторов
                            </div>
                            <div className="admin-contacts__list">
                                {publisher?.admins_contacts?.map(
                                    (contact: InviteLinkAdminContact) => (
                                        <div
                                            className="admin-contacts__list-item"
                                            key={contact.admin_contact_id}
                                        >
                                            <div
                                                onClick={() =>
                                                    handleSelectAdmin(
                                                        contact.admin_contact_id
                                                    )
                                                }
                                                className="admin-contacts__list-item-fullname cursor-pointer"
                                                title={
                                                    contact.telegram_full_name
                                                }
                                            >
                                                {contact.telegram_full_name}
                                            </div>
                                            {contactIdToDelete ===
                                            contact.admin_contact_id ? (
                                                <>
                                                    <span className="admin-contacts__delete-confirmation">
                                                        Удалить?
                                                    </span>
                                                    <div className="admin-contacts__list-item-icons">
                                                        <div
                                                            onClick={
                                                                handleDeleteContact
                                                            }
                                                            className="admin-contacts__delete-confirmation-icon"
                                                        >
                                                            <CheckIcon />
                                                        </div>
                                                        <div
                                                            onClick={() =>
                                                                setContactIdToDelete(
                                                                    null
                                                                )
                                                            }
                                                            className="cursor-pointer pl-[8px]"
                                                        >
                                                            <XIcon />
                                                        </div>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div
                                                        className="admin-contacts__list-item-username"
                                                        title={
                                                            contact.telegram_username ||
                                                            'Нет никнейма'
                                                        }
                                                    >
                                                        {
                                                            contact.telegram_username
                                                        }
                                                    </div>
                                                    <div className="admin-contacts__list-item-icons">
                                                        <a
                                                            href={getLinkToTgUser(
                                                                contact
                                                            )}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            <SquareArrowOutUpRightIcon />
                                                        </a>
                                                        <div
                                                            onClick={() =>
                                                                setContactIdToDelete(
                                                                    contact.admin_contact_id
                                                                )
                                                            }
                                                            className="pl-[8px]"
                                                        >
                                                            <TrashIcon />
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}
